'use client'

import { createContext, useContext, useEffect, useState } from 'react'
import { checkAuthStatusAction } from '@/actions/auth/check-auth-status-action'

type AuthStatus = 'authenticated' | 'unauthenticated' | 'loading'

interface AuthContextType {
  status: AuthStatus
  isAuthenticated: boolean
}

const AuthContext = createContext<AuthContextType>({
  status: 'loading',
  isAuthenticated: false,
})

interface AuthProviderProps {
  children: React.ReactNode
  /**
   * Initial auth status resolved on the server (from cookies).
   * Eliminates the 'loading' flash that caused logged-in users to be
   * prompted for credentials when starting a new course enrolment.
   */
  initialStatus?: AuthStatus
}

export function AuthProvider({
  children,
  initialStatus = 'loading',
}: AuthProviderProps) {
  const [status, setStatus] = useState<AuthStatus>(initialStatus)

  useEffect(() => {
    if (initialStatus !== 'loading') {
      return
    }

    async function check() {
      try {
        const result = await checkAuthStatusAction()
        setStatus(result)
      } catch {
        setStatus('unauthenticated')
      }
    }

    check()
  }, [initialStatus])

  return (
    <AuthContext.Provider
      value={{
        status,
        isAuthenticated: status === 'authenticated',
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuthStatus(): AuthStatus {
  const { status } = useContext(AuthContext)
  return status
}

export function useIsAuthenticated(): boolean {
  const { isAuthenticated } = useContext(AuthContext)
  return isAuthenticated
}
